// components/DateTimePollModal.js - Date/Time Poll Voting Modal
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiCalendar, FiClock, FiCheck } from 'react-icons/fi';
import '../styles/DateTimePollModal.scss';
import { parseLocalDate } from '../shared/eventDateTime';

const DateTimePollModal = ({ pollOptions, onClose, onVote, isVoting }) => {
  const [selectedOptions, setSelectedOptions] = useState([]);

  const handleToggleOption = (optionId) => {
    if (selectedOptions.includes(optionId)) {
      setSelectedOptions(selectedOptions.filter(id => id !== optionId));
    } else {
      setSelectedOptions([...selectedOptions, optionId]);
    }
  };

  const handleSubmit = () => {
    if (selectedOptions.length === 0) {
      alert('Please select at least one date/time option');
      return;
    }
    onVote(selectedOptions);
  };

  const formatDate = (dateString) => {
    const date = (parseLocalDate(dateString) ?? new Date(NaN));
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':');
    const time = new Date();
    time.setHours(parseInt(hours), parseInt(minutes));
    return time.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <AnimatePresence>
      <motion.div
        className="modal-overlay date-time-poll-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="modal-content date-time-poll-modal"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={e => e.stopPropagation()}
        >
          <button className="modal-close" onClick={onClose}>
            <FiX />
          </button>

          <div className="poll-header">
            <FiCalendar size={32} />
            <h2>Choose Your Preferred Date & Time</h2>
            <p>Select one or more options that work for you</p>
          </div>

          <div className="poll-options">
            {pollOptions && pollOptions.length > 0 ? (
              pollOptions.map((option) => (
                <button
                  key={option.id}
                  className={`poll-option ${selectedOptions.includes(option.id) ? 'selected' : ''}`}
                  onClick={() => handleToggleOption(option.id)}
                >
                  <div className="option-content">
                    <div className="option-icon">
                      <FiCalendar />
                    </div>
                    <div className="option-details">
                      <div className="option-date">
                        {formatDate(option.option_date)}
                      </div>
                      {option.option_time && (
                        <div className="option-time">
                          <FiClock size={14} />
                          {formatTime(option.option_time)}
                        </div>
                      )}
                      {option.vote_count > 0 && (
                        <div className="vote-count">
                          {option.vote_count} {option.vote_count === 1 ? 'vote' : 'votes'}
                        </div>
                      )}
                    </div>
                  </div>
                  {selectedOptions.includes(option.id) && (
                    <div className="check-icon">
                      <FiCheck />
                    </div>
                  )}
                </button>
              ))
            ) : (
              <div className="no-options">
                <p>No date/time options available</p>
              </div>
            )}
          </div>

          <div className="poll-footer">
            <button
              className="btn btn-secondary"
              onClick={onClose}
              disabled={isVoting}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={isVoting || selectedOptions.length === 0}
            >
              {isVoting ? (
                <>
                  <div className="spinner-small"></div>
                  Submitting...
                </>
              ) : (
                <>
                  <FiCheck />
                  Submit Votes ({selectedOptions.length})
                </>
              )}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default DateTimePollModal;
